/**
 * @providesModule SKZTrophy
 */

import SKZPrizeBox from 'SKZPrizeBox';
import {PrizeBox, PrizeBoxRecord} from 'PrizeBox';
import _ from 'lodash';

import PROGRESS_BAR_GIFTLESS_ANIMATION_FEATURED from '../Trophies/TrophyAnimations/featured-progress-bar-no-gift.json';
import PROGRESS_BAR_PROGRESS_ANIMATION_FEATURED from '../Trophies/TrophyAnimations/featured-progress-bar-no-shadow.json';
import PROGRESS_BAR_NORMAL_ANIMATION_FEATURED from '../Trophies/TrophyAnimations/featured-progress-bar.json';
import PROGRESS_BAR_GIFTLESS_ANIMATION from '../Trophies/TrophyAnimations/progress-bar-no-gift.json';
import PROGRESS_BAR_PROGRESS_ANIMATION from '../Trophies/TrophyAnimations/progress-bar-no-shadow.json';
import PROGRESS_BAR_NORMAL_ANIMATION from '../Trophies/TrophyAnimations/progress-bar.json';
import moment from 'moment';

type TrophyRecordState = 'UNAVAILABLE' | 'AVAILABLE' | 'AWARDED' | 'CLAIMED';

export interface TrophyRecord {
  id: number;
  name: string;
  description: string;
  prize: SKZPrizeBox;
  prizes: PrizeBoxRecord[];
  picture_url: string;
  progress: number;
  goal: number;
  level: number;
  state: TrophyRecordState;
  deep_link: string | null;
  total_progress: string;
  featured: boolean;
  expiration: string | null;
  max_redemption_count: number | null;
  current_redemption_count: number | null;
  banner_image_url: string | null;
  end_date_visible: boolean | null;
}

const DEFAULT_PRIZEBOX_COLOR = '#4090e2';

export default class Trophy {
  id: number;
  name: string;
  description: string;

  /**
   * @deprecated Use prizes instead
   */
  prize: SKZPrizeBox;

  prizes?: PrizeBoxRecord[];
  picture_url: string;
  progress: number;
  goal: number;
  level: number;
  state: TrophyRecordState;
  deep_link: string | null;
  total_progress: string;
  featured: boolean;
  expiration: string | null;
  max_redemption_count: number | null;
  current_redemption_count: number | null;
  banner_image_url: string | null;
  end_date_visible: boolean | null;

  constructor(keyValueSource: TrophyRecord) {
    Trophy._formatPrizeBox(keyValueSource);
    this.id = keyValueSource.id;
    this.name = keyValueSource.name;
    this.description = keyValueSource.description;
    this.prize = keyValueSource.prize;
    this.prizes = keyValueSource.prizes;
    this.picture_url = keyValueSource.picture_url;
    this.progress = keyValueSource.progress;
    this.goal = keyValueSource.goal;
    this.level = keyValueSource.level;
    this.state = keyValueSource.state;
    this.deep_link = keyValueSource.deep_link;
    this.total_progress = keyValueSource.total_progress;
    this.featured = keyValueSource.featured;
    this.expiration = keyValueSource.expiration;
    this.max_redemption_count = keyValueSource.max_redemption_count;
    this.current_redemption_count = keyValueSource.current_redemption_count;
    this.banner_image_url = keyValueSource.banner_image_url;
    this.end_date_visible = keyValueSource?.end_date_visible;
  }

  static make(trophies: TrophyRecord[]): Trophy[] {
    if (Array.isArray(trophies)) {
      return _.map(trophies, (trophyRecord: TrophyRecord): Trophy => new Trophy(trophyRecord));
    }
    return [];
  }

  /**
   * @deprecated Use getPrizeBoxV2() instead
   */
  static _formatPrizeBox(trophy: TrophyRecord) {
    if (trophy && trophy.prize) {
      trophy.prize = new SKZPrizeBox(trophy.prize);
    }
  }

  isFeatured(): boolean {
    return this.featured;
  }

  /**
   * @deprecated Use getPrizeBoxV2() instead
   */
  getPrizeBox(): SKZPrizeBox {
    return this.prize;
  }

  getPrizeBoxV2(): PrizeBox {
    return {
      color: this.prize.color || DEFAULT_PRIZEBOX_COLOR,
      prizes:
        this.prizes && this.prizes.length > 0
          ? this.prizes
          : [
              {
                amount: this.prize.amount,
                type: this.prize.type,
              } as PrizeBoxRecord,
            ],
    };
  }

  getPrizeTotal(): number {
    return this.prize.getPrizeTotal();
  }

  hasNoPrize(): boolean {
    return this.prizes && this.prizes.length > 0
      ? this.prizes.every((prize: PrizeBoxRecord) => prize.type === 'NONE')
      : this.prize.hasNoPrize();
  }

  isAwarded(): boolean {
    return this.state == 'AWARDED';
  }

  isCashAward(): boolean {
    return this.prizes && this.prizes.length > 0
      ? this.prizes.every((prize: PrizeBoxRecord) => prize.type === 'CASH')
      : this.prize.isCashAward();
  }

  isClaimed(): boolean {
    return this.state == 'CLAIMED';
  }

  isInProgress(): boolean {
    return this.state === 'AVAILABLE';
  }

  isTicketzAward(): boolean {
    return this.prizes && this.prizes.length > 0
      ? this.prizes.every((prize: PrizeBoxRecord) => prize.type === 'TICKETZ')
      : this.prize.isTicketzAward();
  }

  isCompleted(): boolean {
    return this.state === 'AWARDED' || this.state === 'CLAIMED';
  }

  getAnimationForSource(isFeatured: boolean) {
    if (this.isAwarded()) {
      return isFeatured ? PROGRESS_BAR_NORMAL_ANIMATION_FEATURED : PROGRESS_BAR_NORMAL_ANIMATION;
    } else if (this.hasNoPrize()) {
      return isFeatured
        ? PROGRESS_BAR_GIFTLESS_ANIMATION_FEATURED
        : PROGRESS_BAR_GIFTLESS_ANIMATION;
    } else {
      return isFeatured
        ? PROGRESS_BAR_PROGRESS_ANIMATION_FEATURED
        : PROGRESS_BAR_PROGRESS_ANIMATION;
    }
  }

  /**
   * Calculate the trophy progress
   * @returns number
   */
  getProgress() {
    return Math.ceil((this.progress / this.goal) * 100);
  }

  /**
   * Check if timer should be visible
   * @returns boolean
   */
  shouldShowTimer(): boolean {
    return !!this.end_date_visible && this.expiration !== null && this.getTimeRemaining() !== null;
  }

  /**
   * Get time remaining until expiration
   * @param currentTime - Optional timestamp to use as current time (for optimization)
   * @returns object with days, hours, minutes, seconds or null
   */
  getTimeRemaining(
    currentTime?: number
  ): {days: number; hours: number; minutes: number; seconds: number} | null {
    if (!this.expiration) {
      return null;
    }

    const expirationMoment = moment(this.expiration);
    const now = currentTime ? moment(currentTime) : moment();
    const diff = expirationMoment.diff(now);

    if (diff <= 0) {
      return null;
    }

    const duration = moment.duration(diff);

    return {
      days: Math.floor(duration.asDays()),
      hours: duration.hours(),
      minutes: duration.minutes(),
      seconds: duration.seconds(),
    };
  }

  /**
   * Format time remaining as string like "04d 30h 24m" or "45s" when urgent
   * @param currentTime - Optional timestamp to use as current time (for optimization)
   * @returns string | null
   */
  getFormattedTimeRemaining(currentTime?: number): string | null {
    const timeRemaining = this.getTimeRemaining(currentTime);
    if (!timeRemaining) {
      return null;
    }

    const {days, hours, minutes, seconds} = timeRemaining;

    // If less than 60 seconds remaining, show only seconds
    if (days === 0 && hours === 0 && minutes === 0) {
      return `${seconds}s`;
    }

    // Otherwise, show days/hours/minutes format
    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days}d`);
    }
    if (hours > 0 || days > 0) {
      parts.push(`${hours}h`);
    }
    parts.push(`${minutes}m`);

    return parts.length > 0 ? parts.join(' ') : null;
  }
}
